import React from "react";
import "./SectionCard.css";

/**
 * SectionCard — section-card primitive for the customer detail Overview tab.
 *
 * Per CUST-DETAIL-POLISH-003 design spec (`docs/design/CUST-DETAIL-POLISH-003-design.md`).
 *
 * Pattern continuity with VitalCard (-002): both use 8px radius, 1px --border-default,
 * --surface bg. SectionCards are content-driven height with H2 + divider + children.
 *
 * @param {Object} props
 * @param {string} props.heading                              H2 heading text
 * @param {string} props.headingId                            HTML id for the H2 (used by aria-labelledby)
 * @param {React.ReactNode} props.children                    Section content
 * @param {"gold"|"teal"|"default"} [props.accent="default"]  Left accent stripe color
 * @param {boolean} [props.fullWidth=false]                   When true, card spans full grid width (Audit Log)
 * @param {React.ReactNode} [props.actions]                   Right-aligned controls in the heading row
 * @param {boolean} [props.loading=false]                     Render skeleton state in children slot
 * @param {string} [props.error]                              Error message (replaces children with error block)
 * @param {Function} [props.onRetry]                          Retry handler shown when error is set
 * @param {string} [props.className]                          Extra class hook
 */
const SectionCard = ({
  heading,
  headingId,
  children,
  accent = "default",
  fullWidth = false,
  actions,
  loading = false,
  error,
  onRetry,
  className = "",
}) => {
  const accentClass =
    accent === "gold"
      ? "section-card--accent-gold"
      : accent === "teal"
      ? "section-card--accent-teal"
      : "";

  const classes = [
    "section-card",
    accentClass,
    fullWidth ? "section-card--full-width" : "",
    error ? "section-card--error" : "",
    loading ? "section-card--loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={classes}
      aria-labelledby={headingId}
      aria-busy={loading ? "true" : undefined}
    >
      <header className="section-card__header">
        <h2 id={headingId} className="section-card__heading">
          {heading}
        </h2>
        {actions && <div className="section-card__actions">{actions}</div>}
      </header>
      <div className="section-card__divider" aria-hidden="true" />
      <div className="section-card__content">
        {error ? (
          <div className="section-card__error" role="alert">
            <p className="section-card__error-title">Couldn't load this section.</p>
            <p className="section-card__error-message">{error}</p>
            {typeof onRetry === "function" && (
              <button
                type="button"
                className="section-card__retry-btn"
                onClick={onRetry}
              >
                Retry
              </button>
            )}
          </div>
        ) : loading ? (
          <div className="section-card__skeleton-group">
            <div
              className="section-card__skeleton"
              style={{ width: "60%" }}
            />
            <div
              className="section-card__skeleton"
              style={{ width: "90%" }}
            />
            <div
              className="section-card__skeleton"
              style={{ width: "75%" }}
            />
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
};

export default SectionCard;
